import { guid } from "../../../core/helpers/string-util.js";
import Model from "../../Model.js";
import reportColumnFactory from "./columns/reportColumnFactory.js";
import ReportDataSet from "./ReportDataSet.js";
import ReportDisplaySettings from "./ReportDisplaySettings.js";
import reportRowFactory from "./rows/reportRowFactory.js";
import ReportTemplate from "../templates/ReportTemplate.js";
import VisibilityCache from "../VisibilityCache/VisibilityCache.js";

/**
 * Report model.
 *
 * A report is built from the interpretation of a `reportTemplate`.
 * A report is specific to a period and chart of accounts, and holds `rows`
 * and `columns`.
 *
 * Reports are immutable data structures. Their rows, columns, title, and so
 * on cannot be mutated.  There is one exception with their cell data, which
 * can be set through `setData`, because data is always fetched
 * asynchronously.
 *
 * Reports can be visited. All reports and report items implement the
 * visitor pattern `accept` method. Concrete visitors should subclass
 * `reportVisitor`.
 *
 * @param {reportTemplate} [spec.template] - Template used in the report
 * @param {AbstractRow[]} [spec.rows] - Array of all rows of the report
 * @param {abstractColumn[]} [spec.columns] - Array of all columns of the report
 * @param {period} [spec.period] - Period of the report
 * @param {translatedString} [spec.title] - Title of the report
 * @param {string} [spec.description] - Description of the report
 * @param {chartOfAccounts} [spec.chartOfAccounts] - COA for the report
 * @param {specification} [spec.dimensionSpecification] - Dimension specification
 * restricting the report
 * @param {ReportDataSet} [spec.dataSet] - A report's columns and rows don't
 * contain any value: the data set associates a pair (row,column) to the number
 * value of the corresponding cell.
 */
export default class Report extends Model {
	constructor({
		template,
		title,
		description,
		chartOfAccounts,
		period,
		columns = [],
		rows = [],
		dimensionSpecification,
		dataSet,
	} = {}) {
		super(...arguments);
		this._title = title;
		this._description = description;
		this._chartOfAccounts = chartOfAccounts;
		this._period = period;
		this._template = template;
		this._dimensionSpecification = dimensionSpecification;
		this._id = template?.getId() ?? guid();

		if (template) {
			this._displaySettings = template.getDisplaySettings().clone();
		} else {
			this._displaySettings = new ReportDisplaySettings();
		}

		this._rows = [];
		this._columns = [];

		rows.forEach((row) => this._addRow(row));
		columns.forEach((column) => this._addColumn(column));

		this._dataSet = dataSet || new ReportDataSet({ report: this });
		this._isVisibleCache = new VisibilityCache();
	}

	getId() {
		return this._id;
	}

	getTitle() {
		return this._title || "";
	}

	getTitleString() {
		return this.getTitle().toString();
	}

	getDescription() {
		return this._description;
	}

	getPeriod() {
		return this._period;
	}

	getChartOfAccounts() {
		return this._chartOfAccounts;
	}

	getDisplaySettings() {
		return this._displaySettings;
	}

	setDisplaySettings(displaySettings) {
		/*
		 * Since the global visibility is part of the `isVisible` algorithm,
		 * we must flush the cache
		 */
		this.flushRowVisibilityCache();
		this._displaySettings = displaySettings;
	}

	getShowSummaryRows() {
		return this._displaySettings.getShowSummaryRows();
	}

	getAccountVisibility() {
		return this._displaySettings.getAccountVisibility();
	}

	getTemplate() {
		return this._template;
	}

	getRows() {
		return this._rows;
	}

	getRowById(rowId) {
		return this._rows.find((row) => row.getId() === rowId);
	}

	getColumnMatching(predicate) {
		return this._columns.find(predicate);
	}

	getVisibleRows() {
		return this._rows.filter((row) => this.isRowVisible(row));
	}

	isRowVisible(row) {
		return this._isVisibleCache.isVisible(row);
	}

	isColumnVisible(column) {
		return this._isVisibleCache.isVisible(column);
	}

	isItemVisible(item) {
		return this._isVisibleCache.isVisible(item);
	}

	getVisibleChildren({ row }) {
		return this._isVisibleCache.getVisibleChildren({ row });
	}

	hasVisibleChildren({ row }) {
		return this._isVisibleCache.hasVisibleChildren({ row });
	}

	hasSummaryRow({ groupRow }) {
		return (
			this.getShowSummaryRows() &&
			this.hasVisibleChildren({ row: groupRow })
		);
	}

	getColumns() {
		return this._columns;
	}

	getVisibleColumns() {
		return this._columns.filter((column) => this.isColumnVisible(column));
	}

	getLeafVisibleColumns() {
		return this.getLeafColumns().filter((column) =>
			this.isColumnVisible(column),
		);
	}

	getDimensionSpecification() {
		return this._dimensionSpecification;
	}

	/**
	 * Return all rows from the report fetched recursively.
	 *
	 * The result is computed only once and cached afterwards. Because
	 * reports are read-only, there is no need to compute the list
	 * more than once.
	 *
	 * @return {abstractRow[]} - Flatten array of rows.
	 */
	getAllRows() {
		if (!this._cachedAllRows) {
			this._cachedAllRows = this.getRows().slice();

			this._rows.forEach((row) => {
				this._cachedAllRows.push(...row.getAllRows());
			});
		}
		return this._cachedAllRows;
	}

	flushAllRowsCache() {
		this._cachedAllRows = undefined;
		this.flushRowVisibilityCache();
	}

	/**
	 * Return the depth of the report, which is always 0.
	 *
	 * First level rows have a depth of one, their children a depth of 2,
	 * etc.
	 */
	getDepth() {
		return 0;
	}

	getVisibilityDepth() {
		return this.getDepth();
	}

	getCell({ column, row }) {
		return this._dataSet.getCell({ column, row });
	}

	setCellData({ column, row, data }) {
		this.invalidateItemVisibilityCache({ item: row });
		this.invalidateItemVisibilityCache({ item: column });
		this._dataSet.setCellData({ column, row, data });
	}

	flushRowVisibilityCache() {
		this._isVisibleCache.flush();
	}

	invalidateItemVisibilityCache({ item }) {
		this._isVisibleCache.invalidate({ item });
	}

	/**
	 * Return the raw data value for `column` and `row`.
	 */
	getData({ column, row }) {
		return this._dataSet.getData({ column, row });
	}

	getDataForRow(row) {
		return this._dataSet.getDataForRow(row);
	}

	/**
	 * Return the formatted value for `column` and `row`.
	 *
	 * Formatting of the value itself is delegated to the cell, with the
	 * formatter in the `displaySettings` being the default.
	 */
	getFormattedData({ row, column, showDash = false }) {
		return this.getCell({ row, column }).getFormattedData({
			defaultFormatter: this._displaySettings.getFormatter(),
			showDash,
		});
	}

	/**
	 * Set cell `data` in the report.
	 *
	 * Existing data is not flushed, so if some new `data` does not
	 * erase existing one, the current data is kept.
	 */
	setData({ data }) {
		const columns = this.getLeafColumns();

		this._rows.forEach((row, index) => {
			if (!row.canBeCalculated()) {
				return;
			}

			this.invalidateItemVisibilityCache({ item: row });

			row.setData({
				data: data[index],
				columns,
			});
		});
	}

	getLeafColumns() {
		return this._columns.reduce((result, column) => {
			result.push(...column.getLeaves());
			return result;
		}, []);
	}

	getSerializableLeafColumns() {
		return this._columns.flatMap((column) =>
			column.getSerializableLeaves(),
		);
	}

	isIncomeStatement() {
		return this._template?.isIncomeStatement();
	}

	isBalanceSheet() {
		return this._template?.isBalanceSheet();
	}

	isCashFlow() {
		return this._template?.isCashFlow();
	}

	expandRowsUpToDefaultLevel() {
		this.expandRowsUpToLevel(
			this._displaySettings.getDefaultExpansionLevel(),
		);
	}

	expandRowsUpToLevel(level) {
		this.expandRows((row) => row.getDepth() < level);
	}

	expandRows(predicate) {
		this.getAllRows()
			.filter((row) => row.isGroupRow())
			.forEach((row) => {
				row.setCollapsed(!predicate(row));
			});
	}

	/**
	 * Copies another report expansion tree so it looks exactly the
	 * same for the user. The current report and the copy must have
	 * the same rows in the same order.
	 */
	expandAccordingTo(anotherReport) {
		const referenceRows = anotherReport.getAllRows();

		this.getAllRows().forEach((row, index) => {
			row.expandAccordingTo(referenceRows[index]);
		});
	}

	findItemWithTemplate(template) {
		return this.getColumns()
			.concat(this.getAllRows())
			.find((item) => item.getTemplate() === template);
	}

	findItemWithItemTemplateId(itemTemplateId) {
		const itemTemplate = this.getTemplate().getItemById(itemTemplateId);

		return itemTemplate && this.findItemWithTemplate(itemTemplate);
	}

	hasDataForRow(row) {
		return this._dataSet.hasDataForRow(row);
	}

	hasDataForColumn(column) {
		return this._dataSet.hasDataForColumn(column);
	}

	isDebitPositive() {
		return this._displaySettings.getDebitPositive();
	}

	isNegated() {
		return !this.isDebitPositive();
	}

	isLandscape() {
		return this._displaySettings.getLandscape();
	}

	isRow() {
		return false;
	}

	isCollapsed() {
		return false;
	}

	isDisplaySettingsShowingRow(row) {
		return this._displaySettings.getAccountVisibility().isVisible(row);
	}

	isCloneRow() {
		return false;
	}

	hasDimensionRow() {
		return this.getAllRows().some((row) =>
			row.hasOwnDimensionSpecification(),
		);
	}

	hasDimensionSpecification() {
		return !!this._dimensionSpecification;
	}

	shouldPrintLogo() {
		return this.getDisplaySettings().getShowLogo();
	}

	validate() {
		super.validate(...arguments);

		this.getAllRows().forEach((row) => row.validate(...arguments));
		this.getColumns().forEach((column) => column.validate(...arguments));
	}

	/**
	 * Visiting method.  See `reportVisitor` for all visiting methods.
	 */
	accept(visitor) {
		return visitor.visitReport(this);
	}

	withCoALanguage(callback) {
		if (this._chartOfAccounts) {
			this._chartOfAccounts.withLanguage(callback);
		} else {
			callback();
		}
	}

	findPreviousVisibleEditableColumnFrom(column) {
		let previousColumn = this._findPreviousColumnFrom(column);
		while (
			!this.isColumnVisible(previousColumn) ||
			!previousColumn.isEditableBudgetColumn()
		) {
			previousColumn = this._findPreviousColumnFrom(previousColumn);
			if (column === previousColumn) {
				return column;
			}
		}
		return previousColumn;
	}

	findPreviousVisibleAccountRowFrom(row) {
		let previousRow = this._findPreviousRowFrom(row);
		while (!this.isRowVisible(previousRow) || !previousRow.isAccountRow()) {
			previousRow = this._findPreviousRowFrom(previousRow);
		}
		return previousRow;
	}

	findNextVisibleEditableColumnFrom(column) {
		let nextColumn = this._findNextColumnFrom(column);
		while (
			!this.isColumnVisible(nextColumn) ||
			!nextColumn.isEditableBudgetColumn()
		) {
			nextColumn = this._findNextColumnFrom(nextColumn);
		}
		return nextColumn;
	}

	findNextVisibleAccountRowFrom(row) {
		let nextRow = this._findNextRowFrom(row);
		while (!this.isRowVisible(nextRow) || !nextRow.isAccountRow()) {
			nextRow = this._findNextRowFrom(nextRow);
		}
		return nextRow;
	}

	shouldFormatInThousands() {
		return false;
	}

	isEmpty() {
		return false;
	}

	_findNextRowFrom(row) {
		const allRows = this.getAllRows();
		const nextRowIndex = (allRows.indexOf(row) + 1) % allRows.length;
		return allRows[nextRowIndex];
	}

	_findNextColumnFrom(column) {
		const allColumns = this.getColumns();
		const nextColumnIndex =
			(allColumns.indexOf(column) + 1) % allColumns.length;
		return allColumns[nextColumnIndex];
	}

	_findPreviousRowFrom(row) {
		const allRows = this.getAllRows();
		let previousRowIndex = allRows.indexOf(row) - 1;
		if (previousRowIndex < 0) {
			previousRowIndex = allRows.length - 1;
		}
		return allRows[previousRowIndex];
	}

	_findPreviousColumnFrom(column) {
		const allColumns = this.getColumns();
		let previousColumnIndex = allColumns.indexOf(column) - 1;
		if (previousColumnIndex < 0) {
			previousColumnIndex = allColumns.length - 1;
		}
		return allColumns[previousColumnIndex];
	}

	/**
	 * Add `row` as a direct child (first level row).
	 * This method should only be called upon initialization.
	 */
	_addRow(row) {
		this._rows.push(row);
		row.setReport(this);
	}

	/**
	 * Add `column`.
	 * This method should only be called upon initialization.
	 */
	_addColumn(column) {
		this._columns.push(column);
		column.setReport(this);
	}

	/**
	 * Report serialization is only done today for data calculation
	 * requests.
	 */
	_serializedProperties() {
		return {
			rows: this._rows,
			columns: this.getLeafColumns(),
			template: this._template,
		};
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		if (data.template) {
			data.template = ReportTemplate.materialize(data.template, context);
			context.setExtraProperty("template", data.template);
		}

		data.rows = reportRowFactory.materializeAll(data.rows, context);

		data.columns = reportColumnFactory.materializeAll(
			data.columns,
			context,
		);
	}

	static buildFrom(data) {
		const report = super.buildFrom(data);

		report.setData({ data: data.cells });

		return report;
	}
}
